import React, { useState, useEffect } from 'react';
import axios from 'axios';
import useOnboardingProgress, { getCompletedTasks } from './useOnboardingProgress';
import { GetStartedChecklist } from './GetStartedPopup';
import { Dropdown, Modal, Ratio } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGraduationCap,
  faPlay,
  faCompass,
  faCheck
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { RESTART_TOUR_EVENT } from './OnboardingTour';
import { VIDEO_EMBED_URL } from 'config';


const AUTOPLAY_VIDEO_URL = `${VIDEO_EMBED_URL}?autoplay=1&mute=1&rel=0`;

const isActivationKey = (e) => e.key === 'Enter' || e.key === ' ';
const RetakeTourButton = () => {
  const { t } = useTranslation();
  const [showMenu, setShowMenu] = useState(false);
  const [menuView, setMenuView] = useState('guide'); 
  const [showVideoModal, setShowVideoModal] = useState(false);

  const [hasWatchedTutorial, setHasWatchedTutorial] = useState(
    () => localStorage.getItem('hasWatchedTutorial') === 'true'
  );
  const [hasCompletedTour, setHasCompletedTour] = useState(
    () =>
      localStorage.getItem('hasCompletedTourGuide') === 'true' ||
      localStorage.getItem('hasCompletedGuidedTour') === 'true'
  );

  const { completedTasks, completedCount: completed, totalCount: total, progressPct: pct } =
    useOnboardingProgress();

  const isProfileDone = completedTasks?.includes('profile');

  const [isExpanded, setIsExpanded] = useState(() => {
    const isDismissed = localStorage.getItem('onboarding_100_dismissed') === 'true';
    if (isDismissed || pct >= 100) return false;
    const initialTasks = getCompletedTasks();
    return initialTasks.includes('profile');
  });
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    const isDismissed = localStorage.getItem('onboarding_100_dismissed') === 'true';
    if (isProfileDone && pct < 100 && !isDismissed) {
      setIsClosing(false);
      setIsExpanded(true);
    }
  }, [isProfileDone, pct]);

  useEffect(() => {
    const handleActiveChange = () => {
      const tasks = getCompletedTasks();
      if (tasks.includes('profile') && pct < 100) {
        setIsClosing(false);
        setIsExpanded(true);
      }
    };
    const handleOpen = () => {
      const tasks = getCompletedTasks();
      if (tasks.includes('profile') && pct < 100) {
        setIsClosing(false);
        setIsExpanded(true);
      }
      setMenuView('checklist');
      setShowMenu(true);
    };

    window.addEventListener('onboarding:activeStateChanged', handleActiveChange);
    window.addEventListener('onboarding:openGetStarted', handleOpen);
    return () => {
      window.removeEventListener('onboarding:activeStateChanged', handleActiveChange);
      window.removeEventListener('onboarding:openGetStarted', handleOpen);
    };
  }, [pct]);

  useEffect(() => {
    if (pct < 100) {
      localStorage.removeItem('onboarding_100_dismissed');
    } else if (pct >= 100 && total > 0 && isExpanded && !isClosing) {
      const timer = setTimeout(() => {
        setIsClosing(true);
        setTimeout(() => {
          setIsExpanded(false);
          setIsClosing(false);
          localStorage.setItem('onboarding_100_dismissed', 'true');
        }, 450);
      }, 8000);

      return () => clearTimeout(timer);
    }
  }, [pct, total, isExpanded, isClosing]);

  useEffect(() => {
    const handleUpdate = () => {
      setHasWatchedTutorial(
        localStorage.getItem('hasWatchedTutorial') === 'true'
      );
      setHasCompletedTour(
        localStorage.getItem('hasCompletedTourGuide') === 'true' ||
        localStorage.getItem('hasCompletedGuidedTour') === 'true'
      );
    };

    window.addEventListener('onboarding:tutorialWatched', handleUpdate);
    window.addEventListener('onboarding:tourCompleted', handleUpdate);
    return () => {
      window.removeEventListener('onboarding:tutorialWatched', handleUpdate);
      window.removeEventListener('onboarding:tourCompleted', handleUpdate);
    };
  }, []);

  const handleWatchTutorial = () => {
    setShowMenu(false);
    setShowVideoModal(true);
  };

  const handleCloseVideoModal = () => {
    setShowVideoModal(false);
    localStorage.setItem('hasWatchedTutorial', 'true');
    setHasWatchedTutorial(true);
    window.dispatchEvent(new Event('onboarding:tutorialWatched'));

    const userId = localStorage.getItem('UserId');
    if (userId) {
      axios.patch(
        `${process.env.REACT_APP_API_URL}/userRoute/updateTutorialWatchedStatus/${userId}`,
        {},
        { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
      ).catch((err) => console.warn('[onboarding] Failed to persist tutorial status:', err));
    }
  };

  const handleGuidedTour = () => {
    setShowMenu(false);
    window.dispatchEvent(new Event(RESTART_TOUR_EVENT));
  };

  const handleContinueGettingStarted = () => {
    if (getCompletedTasks().includes('profile') && pct < 100) {
      setIsClosing(false);
      setIsExpanded(true);
    }
    setMenuView('checklist');
    setShowMenu(true);
  };

  return (
    <>

      <Dropdown
        show={showMenu}
        onToggle={(isOpen) => {
          setShowMenu(isOpen);
          if (isOpen) {
            setMenuView('guide');
          }
        }}
        align="end"
        className="d-inline-block position-relative"
      >
        <Dropdown.Toggle
          as="button"
          type="button"
          className={`hat-tour-btn d-inline-flex align-items-center border-0 shadow-none pointer text-reset ${
            isExpanded
              ? 'is-expanded bg-primary-subtle rounded-pill'
              : ''
          }`}
          title={t('getPlanGuide')}
          aria-label={t('getPlanGuide')}
          data-tour="tour-retake"
        >
          <FontAwesomeIcon
            icon={faGraduationCap}
            size="lg"
          />
          {isExpanded && (
            <span
              className={`hat-progress-content ${isClosing ? 'closing-left' : ''}`}
            >
              <span className="ms-2 fw-bold text-primary font-size-13 font-inter">
                {pct}% &bull; {completed}/{total}
              </span>
            </span>
          )}
        </Dropdown.Toggle>

        <Dropdown.Menu
          align="end"
          className={`border border-200 shadow-sm rounded-4 p-0 mt-2 overflow-hidden ${
            menuView === 'checklist' ? 'get-started-menu' : 'getplan-guide-menu'
          }`}
          renderOnMount
        >
          {menuView === 'checklist' ? (
            <GetStartedChecklist
              onClose={() => setShowMenu(false)}
              onBack={() => setMenuView('guide')}
            />
          ) : (
            <>
              <div className="px-3 pt-3 pb-2 border-bottom border-200">
                <div className="fw-bold text-700 font-size-14 font-inter text-nowrap">
                  {t('getPlanGuide')}
                </div>
              </div>

              <div className="p-2 d-flex flex-column gap-1">
            <div
              className="d-flex align-items-center justify-content-between px-3 py-2 rounded-3 cursor-pointer hover-bg-100 transition-base text-nowrap"
              onClick={handleWatchTutorial}
              onKeyDown={(e) => {
                if (isActivationKey(e)) {
                  e.preventDefault();
                  handleWatchTutorial();
                }
              }}
              role="button"
              tabIndex={0}
            >
              <div className="d-flex align-items-center">
                <div className="icon-item icon-item-sm bg-primary-subtle text-primary rounded-3 me-3 flex-shrink-0 shadow-none">
                  <FontAwesomeIcon icon={faPlay} className="font-size-11" />
                </div>
                <span className="fw-semibold text-900 font-size-14 font-inter">
                  {t('watchTutorial')}
                </span>
              </div>
              {hasWatchedTutorial && (
                <div className="text-success ms-2 flex-shrink-0">
                  <FontAwesomeIcon icon={faCheck} className="font-size-12" />
                </div>
              )}
            </div>

            <div
              className="d-flex align-items-center justify-content-between px-3 py-2 rounded-3 cursor-pointer hover-bg-100 transition-base text-nowrap"
              onClick={handleGuidedTour}
              onKeyDown={(e) => {
                if (isActivationKey(e)) {
                  e.preventDefault();
                  handleGuidedTour();
                }
              }}
              role="button"
              tabIndex={0}
            >
              <div className="d-flex align-items-center">
                <div className="icon-item icon-item-sm bg-primary-subtle text-primary rounded-3 me-3 flex-shrink-0 shadow-none">
                  <FontAwesomeIcon icon={faCompass} className="font-size-13" />
                </div>
                <span className="fw-semibold text-900 font-size-14 font-inter">
                  {t('guidedTour')}
                </span>
              </div>
              {hasCompletedTour && (
                <div className="text-success ms-2 flex-shrink-0">
                  <FontAwesomeIcon icon={faCheck} className="font-size-12" />
                </div>
              )}
            </div>

            <div
              className="d-flex align-items-center justify-content-between px-3 py-2 rounded-3 cursor-pointer hover-bg-100 transition-base text-nowrap"
              onClick={handleContinueGettingStarted}
              onKeyDown={(e) => {
                if (isActivationKey(e)) {
                  e.preventDefault();
                  handleContinueGettingStarted();
                }
              }}
              role="button"
              tabIndex={0}
            >
              <div className="d-flex align-items-center">
                <div className="icon-item icon-item-sm bg-primary-subtle text-primary rounded-3 me-3 flex-shrink-0 shadow-none">
                  <FontAwesomeIcon icon={faCheck} className="font-size-12" />
                </div>
                <div className="d-flex flex-column text-nowrap">
                  <span className="fw-semibold text-900 font-size-14 font-inter lh-sm">
                    {t('onboardingTour.getStartedTitle', 'Get Started')}
                  </span>
                  <span className="text-primary fw-semibold font-size-12 font-inter mt-1">
                    {completed} {t('ofText')} {total} {t('completedText')} &bull; {pct}%
                  </span>
                </div>
              </div>
              {pct >= 100 && (
                <div className="text-success ms-2 flex-shrink-0">
                  <FontAwesomeIcon icon={faCheck} className="font-size-12" />
                </div>
              )}
              </div>
            </div>
          </>
        )}
        </Dropdown.Menu>
      </Dropdown>

      <Modal
        show={showVideoModal}
        onHide={handleCloseVideoModal}
        size="lg"
        centered
        dialogClassName="modal-dialog-centered"
        contentClassName="border-0 rounded-4 shadow-lg overflow-hidden position-relative"
      >
        <button
          type="button"
          className="btn-close font-size-11 position-absolute top-0 end-0 m-3 z-1"
          onClick={handleCloseVideoModal}
          aria-label={t('close', 'Close')}
        />

        <Modal.Body className="p-3 p-sm-4 text-center">
          {/* Modal Titles */}
          <h4 className="fw-bold text-900 mb-1">
            {t('productTutorial')}
          </h4>
          <p className="text-secondary fw-normal font-size-13 mb-2">
            {t('productTutorialSubtitle')}
          </p>

          <div className="mt-2">
            <Ratio aspectRatio="16x9" className="rounded-3 overflow-hidden bg-black shadow-sm">
              <iframe
                src={AUTOPLAY_VIDEO_URL}
                title={t('productTutorial')}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </Ratio>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default RetakeTourButton;